import React from "react";
import {
  Anchor,
  Avatar,
  Badge,
  Button,
  Group,
  Stack,
  Text,
} from "@mantine/core";
import { IconMessage } from "@tabler/icons-react";
import { formatPhoneNumber } from "../../utils/phoneUtils";
import { formatIsoDateLong, ageFromIsoDate } from "../../utils/localDateUtils";
import { Client } from "../../types/client";
import PopupNotesAlert from "./PopupNotesAlert";
import styles from "./ClientProfileHeader.module.css";

/** Loyalty block data for the drawer header (loyalty FE1 §4.1). */
export interface ClientLoyaltyHeaderData {
  /** Account exists ∧ opted_in. */
  enrolled: boolean;
  pointsBalance: number;
  onEditPoints: () => void;
}

interface ClientProfileHeaderProps {
  client: Client;
  canEdit: boolean;
  canDelete: boolean;
  onEditProfile: () => void;
  onDeleteClient: () => void;
  /** Show a "Message" action (two-way SMS). Gated + wired by the parent. */
  canMessage?: boolean;
  onMessage?: () => void;
  /** Present only when the location's loyalty program is enabled. */
  loyalty?: ClientLoyaltyHeaderData | null;
}

function getInitials(client: Client): string {
  const a = client.first_name?.trim().charAt(0) ?? "";
  const b = client.last_name?.trim().charAt(0) ?? "";
  return (a + b).toUpperCase() || "?";
}

export default function ClientProfileHeader({
  client,
  canEdit,
  canDelete,
  onEditProfile,
  onDeleteClient,
  canMessage = false,
  onMessage,
  loyalty = null,
}: ClientProfileHeaderProps) {
  const fullName = `${client.first_name} ${client.last_name}`.trim();
  const phone = formatPhoneNumber(`${client.area_code}${client.phone_number}`);
  const pronouns = client.pronouns?.trim();
  const gender = client.gender?.trim();

  const nameSuffixParts = [pronouns, gender].filter(Boolean);
  const nameSuffix =
    nameSuffixParts.length > 0 ? ` (${nameSuffixParts.join(", ")})` : null;

  const birthdayDisplay = client.birthday
    ? (() => {
        // Birthday is a date-only value: format + age from the YYYY-MM-DD
        // string directly so it renders identically in every timezone.
        const formatted = formatIsoDateLong(client.birthday);
        const age = ageFromIsoDate(client.birthday);
        return age != null ? `${formatted} (${age} yrs)` : formatted;
      })()
    : "—";

  const cityStateZip = [
    client.city?.trim(),
    [client.state?.trim(), client.zip_code?.trim()].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");

  const addressDisplay =
    [client.address_1?.trim(), client.address_2?.trim(), cityStateZip || null]
      .filter(Boolean)
      .join(", ") || "—";

  return (
    <Stack gap="md" className={styles.card}>
      <Group
        justify="space-between"
        align="flex-start"
        wrap="wrap"
        gap="lg"
        className={styles.row}
      >
        <Group gap="lg" wrap="nowrap" align="flex-start">
          <Avatar
            color="teal"
            radius="xl"
            size={80}
            className={styles.avatar}
            aria-hidden
          >
            {getInitials(client)}
          </Avatar>
          <Stack gap="xs" className={styles.meta}>
            <Group gap="sm" wrap="wrap" align="center">
              <Text className={styles.name}>
                {fullName}
                {nameSuffix && (
                  <Text span className={styles.pronouns} component="span">
                    {nameSuffix}
                  </Text>
                )}
              </Text>
              <Badge
                variant="light"
                color="lime"
                size="lg"
                className={styles.statusBadge}
                tt="none"
              >
                Active
              </Badge>
              {loyalty?.enrolled && (
                <Badge
                  variant="light"
                  color="var(--brand-teal)"
                  size="lg"
                  tt="none"
                >
                  Enrolled
                </Badge>
              )}
            </Group>
            {loyalty?.enrolled && (
              <Group gap="md" wrap="wrap" align="center">
                <Text size="sm" fw={600}>
                  {loyalty.pointsBalance.toLocaleString("en-US")} points
                </Text>
                <Anchor
                  component="button"
                  type="button"
                  size="sm"
                  fw={600}
                  onClick={loyalty.onEditPoints}
                >
                  Edit Points
                </Anchor>
              </Group>
            )}
            <Group gap="xl" wrap="wrap" className={styles.contactLine}>
              <Text size="sm" c="dimmed">
                <Text span fw={600} c="dark.6" component="span">
                  Phone:{" "}
                </Text>
                {phone}
              </Text>
              <Text size="sm" c="dimmed">
                <Text span fw={600} c="dark.6" component="span">
                  Email:{" "}
                </Text>
                {client.email || "—"}
              </Text>
            </Group>
            <Group gap="xl" wrap="wrap" className={styles.contactLine}>
              <Text size="sm" c="dimmed">
                <Text span fw={600} c="dark.6" component="span">
                  Birthday:{" "}
                </Text>
                {birthdayDisplay}
              </Text>
              <Text size="sm" c="dimmed">
                <Text span fw={600} c="dark.6" component="span">
                  Address:{" "}
                </Text>
                {addressDisplay}
              </Text>
            </Group>
          </Stack>
        </Group>
        <Group gap="xs" className={styles.actions}>
          {canMessage && onMessage && (
            <Button
              variant="light"
              color="teal"
              leftSection={<IconMessage size={16} />}
              onClick={onMessage}
            >
              Message
            </Button>
          )}
          {canEdit && (
            <Button
              variant="default"
              color="teal"
              onClick={onEditProfile}
              className={styles.editBtn}
            >
              Edit
            </Button>
          )}
          {canDelete && (
            <Button
              variant="subtle"
              color="red"
              onClick={onDeleteClient}
              className={styles.deleteBtn}
            >
              Delete
            </Button>
          )}
        </Group>
      </Group>
      <PopupNotesAlert notes={client.notes} />
    </Stack>
  );
}
